import React from "react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Header */}
        <div className="mb-10">
          <a href="/" className="inline-block mb-6">
            <img src="/logo-dark.svg" alt="Relay" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Privacy Policy</h1>
          <p className="text-slate-500 mt-2">Last updated: May 21, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed text-[15px]">

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Introduction</h2>
            <p>
              Relay ("we," "our," or "the Service") is an SMS marketing platform operated by MVSS Studio
              for real estate professionals using FollowUp Boss CRM. This Privacy Policy explains how we collect,
              use, store, and protect your information when you use our web application, Chrome extension,
              and FollowUp Boss embedded widget.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Information We Collect</h2>

            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">Account Information</h3>
            <p>
              When you create an account, we collect your name, email address, and a password (stored as a
              one-way hash — we never store plaintext passwords). If you join a team, we store your team
              membership and role.
            </p>

            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">CRM Contact Data</h3>
            <p>
              When you connect your FollowUp Boss account and sync contacts, we import contact records
              including: first name, last name, phone number, email address, lead stage, tags, and source.
              This data is used solely to enable SMS messaging features and is synced on demand (not
              continuously).
            </p>

            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">Message Data</h3>
            <p>
              We store records of SMS messages sent and received through the Service, including message
              content, timestamps, delivery status, and the associated contact. This data is necessary
              to provide the inbox, campaign tracking, and delivery reporting features.
            </p>

            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">API Credentials</h3>
            <p>
              You provide API keys for FollowUp Boss and SignalWire to enable CRM integration and SMS
              delivery. These credentials are stored encrypted in our database and are never shared with
              third parties.
            </p>

            <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">Chrome Extension</h3>
            <p>
              The Relay Chrome extension only activates on FollowUp Boss pages (followupboss.com).
              It does not collect browsing history, track activity on other websites, or access any data
              outside of the FollowUp Boss interface. The extension communicates solely with the FollowUp
              SMS backend to load contacts and send messages.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
              <li>Authenticate your account and manage sessions</li>
              <li>Sync and display your CRM contacts for audience selection</li>
              <li>Send, receive, and track SMS messages on your behalf</li>
              <li>Execute automated drip campaigns you configure</li>
              <li>Provide inbox, delivery status, and analytics features</li>
              <li>Process opt-out requests (STOP keyword compliance)</li>
              <li>Send you push notifications about inbound replies (if enabled)</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell, rent, or share your data with third parties for marketing
              purposes. We do not use your data to train AI models.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Third-Party Services</h2>
            <p>The Service integrates with the following third-party providers to function:</p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
              <li>
                <strong>FollowUp Boss</strong> — CRM data sync (contacts, Smart Lists). Data flows
                between your FUB account and Relay via their API using your API key.
              </li>
              <li>
                <strong>SignalWire</strong> — SMS delivery and receipt. Message content is transmitted
                to SignalWire for delivery to recipients' phone numbers. SignalWire's privacy policy
                applies to their handling of message data.
              </li>
              <li>
                <strong>MongoDB Atlas</strong> — Database hosting. All application data is stored in
                MongoDB Atlas with encryption at rest and in transit.
              </li>
              <li>
                <strong>Railway</strong> — Application hosting. The Service runs on Railway's
                infrastructure.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Data Storage and Security</h2>
            <p>
              All data is stored in MongoDB Atlas with encryption at rest (AES-256) and encryption in
              transit (TLS 1.2+). Passwords are hashed using bcrypt. API credentials are stored with
              encryption. Authentication uses HTTP-only, secure cookies with JWT tokens.
            </p>
            <p className="mt-2">
              We implement reasonable security measures to protect your data, but no method of
              transmission or storage is 100% secure. If you discover a security vulnerability,
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">6. SMS Compliance</h2>
            <p>
              Relay is designed to support compliance with SMS messaging regulations:
            </p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
              <li>
                <strong>Opt-out handling:</strong> Contacts who reply "STOP" are automatically marked
                as opted out and excluded from all future messages.
              </li>
              <li>
                <strong>10DLC registration:</strong> We support and require 10DLC campaign registration
                through SignalWire for compliant business messaging.
              </li>
              <li>
                <strong>Consent:</strong> You are responsible for ensuring you have proper consent from
                contacts before sending SMS messages. Relay provides the tools — compliance with
                TCPA, CAN-SPAM, and other regulations is the responsibility of the account holder.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Data Retention</h2>
            <p>
              We retain your data for as long as your account is active. Message logs are retained
              indefinitely to support inbox history and analytics. If you request account deletion,
              we will delete your account data, contact records, and message logs within 30 days.
              Some data may be retained in encrypted backups for up to 90 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 ml-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your account and associated data</li>
              <li>Export your contact and message data</li>
              <li>Withdraw consent for push notifications at any time</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, contact us at the email address below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Cookies</h2>
            <p>
              We use a single HTTP-only session cookie for authentication. We do not use tracking
              cookies, analytics cookies, or any third-party advertising cookies. No cookie consent
              banner is needed because our cookie is strictly necessary for the Service to function.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Children's Privacy</h2>
            <p>
              The Service is intended for use by real estate professionals and is not directed at
              children under 13. We do not knowingly collect information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page
              with an updated "Last updated" date. Continued use of the Service after changes constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">12. Contact</h2>
            <p>
              If you have questions about this Privacy Policy or your data, contact us at:
            </p>
            <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="font-semibold text-slate-900">MVSS Studio</p>
              <p>Las Vegas, NV</p>
              <p className="mt-2">
                Email: <a href="mailto:design@mvss.studio" className="text-[#FF5C35] hover:text-[#E84E29]">design@mvss.studio</a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-200 text-center text-sm text-slate-400">
          &copy; {new Date().getFullYear()} Relay &middot;middot; MVSS Studio &middot; Petrosian Team
        </div>

      </div>
    </div>
  );
}
